import {
  Button,
  Collapse,
  Tooltip,
  Typography,
  Box,
  Divider,
  Paper,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { CustomSpinner } from "../CustomSpinner";
import {
  SimpleSelectOption,
  useActionOptions,
  useMatchOptions,
  useMatchTypeOptions,
  useTagOptions,
  useTeamOptions,
} from "../simple-select/simpleSelectOptions";
import { AdvancedFilters } from "./AdvancedFilters";
import { SingleSelect } from "../simple-select/SimpleSelect";
import { ChipSelect } from "../simple-select/ChipSelect";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import { getFilteredEventFilters } from "../../requests/matchEvents";
import { useGetMatch } from "../../requests/matches";
import { borderRadius, shadows } from "../../utils/designSystem";

export type EventFilters = {
  matchId: string;
  eventId: string;
  team1Id: string;
  team2Id: string;
  possessionTeamId: string;
  defenseTeamId: string;
  matchTypeId: string;
  tags: string[];
  previousActionIds: string[];
  actionIds: string[];
  nextActionIds: string[];
  commentRegex: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  pitchXMin: string;
  pitchXMax: string;
  pitchYMin: string;
  pitchYMax: string;
  hasComments: string;
};

type Props = {
  isAdvancedFilters: boolean;
  setIsAdvancedFilters: Dispatch<SetStateAction<boolean>>;
  eventFilters: EventFilters;
  setEventFilters: Dispatch<SetStateAction<EventFilters>>;
  setIsSearchEditMode: Dispatch<SetStateAction<boolean>>;
};

export const VideoReviewForm = ({
  isAdvancedFilters,
  setIsAdvancedFilters,
  eventFilters,
  setEventFilters: _setEventFilters,
  setIsSearchEditMode,
}: Props) => {
  const navigate = useNavigate();

  const getMatch = useGetMatch();

  const teamOptions = useTeamOptions();
  const matchOptions = useMatchOptions();
  const matchTypeOptions = useMatchTypeOptions();
  const tagOptions = useTagOptions();
  const actionOptions = useActionOptions();

  const setEventFilters = (updatedFilters: Partial<EventFilters>): void =>
    _setEventFilters((prevState) => ({
      ...prevState,
      ...updatedFilters,
    }));

  const setEventFilter = (
    key: keyof EventFilters,
    previousIds: (number | string)[],
    options: SimpleSelectOption[],
    getValues: (prevState: SimpleSelectOption[]) => SimpleSelectOption[]
  ) =>
    setEventFilters({
      [key]: getValues(
        previousIds.map(
          (actionId) => options.find((option) => actionId === option.id) as SimpleSelectOption
        )
      ).map((option) => option.id),
    });

  if (!teamOptions || !matchOptions || !matchTypeOptions || !tagOptions || !actionOptions) {
    return <CustomSpinner />;
  }

  const possessionOptions =
    eventFilters.team1Id && eventFilters.team2Id
      ? teamOptions.filter((option) =>
          [eventFilters.team1Id, eventFilters.team2Id].includes(option.id)
        )
      : teamOptions;

  const defenseOptions =
    eventFilters.team1Id && eventFilters.team2Id
      ? teamOptions.filter((option) =>
          [eventFilters.team1Id, eventFilters.team2Id].includes(option.id)
        )
      : teamOptions;

  const SearchButton = () => {
    const disabled = getFilteredEventFilters(eventFilters).length === 0;
    return (
      <Tooltip
        // Zero-length string will cause the tooltip to be disabled.
        title={disabled ? "At least one filter must be chosen." : ""}
        enterDelay={0}
        followCursor={true}
      >
        <span>
          <Button
            onClick={() => {
              const searchParams = new URLSearchParams();
              getFilteredEventFilters(eventFilters).forEach(([key, value]) =>
                searchParams.append(key, value)
              );
              navigate(`/video-review?${searchParams.toString()}`);
              setIsSearchEditMode(false);
            }}
            variant="contained"
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 600,
              borderRadius: borderRadius.md,
              boxShadow: shadows.md,
              "&:hover": {
                boxShadow: shadows.lg,
              },
            }}
            disabled={disabled}
          >
            Search Events
          </Button>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: borderRadius.lg,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          boxShadow: shadows.sm,
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: "text.primary",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Basic Filters
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={12} md={2}>
            <SingleSelect
              value={eventFilters.matchId}
              setValue={(value) => {
                // If the match is selected, set team1 and team2 to the selected match's teams.
                if (value.length === 0) {
                  setEventFilters({
                    matchId: value,
                    team1Id: "",
                    team2Id: "",
                  });
                } else {
                  const match = getMatch(parseInt(value));
                  const team1Id = match?.home_team_id.toString() ?? "";
                  const team2Id = match?.away_team_id.toString() ?? "";
                  const possessionTeamId =
                    eventFilters.possessionTeamId === team1Id ||
                    eventFilters.possessionTeamId === team2Id
                      ? eventFilters.possessionTeamId
                      : "";
                  setEventFilters({
                    matchId: value,
                    team1Id,
                    team2Id,
                    possessionTeamId,
                  });
                }

                // If the match is unset, reset team1 and team2 if both chosen.
              }}
              label="Match"
              options={matchOptions}
              none
              widthFactor="100%"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SingleSelect
              value={eventFilters.possessionTeamId}
              setValue={(value) => setEventFilters({ possessionTeamId: value })}
              label="Possession Team"
              options={possessionOptions}
              none
              widthFactor="100%"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SingleSelect
              value={eventFilters.defenseTeamId}
              setValue={(value) => setEventFilters({ defenseTeamId: value })}
              label="Defense Team"
              options={defenseOptions}
              none
              widthFactor="100%"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <ChipSelect
              label="Actions"
              widthFactor="100%"
              options={actionOptions}
              values={eventFilters.actionIds.map(
                (actionId) =>
                  actionOptions.find(
                    (actionOption) => actionId === actionOption.id
                  ) as SimpleSelectOption
              )}
              setValues={(_getValues: (prevState: SimpleSelectOption[]) => SimpleSelectOption[]) =>
                setEventFilter("actionIds", eventFilters.actionIds, actionOptions, _getValues)
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <ChipSelect
              label="Tags"
              widthFactor="100%"
              options={tagOptions}
              values={eventFilters.tags?.map(
                (tag) => tagOptions.find((tagOption) => tag === tagOption.id) as SimpleSelectOption
              )}
              setValues={(_getValues: (prevState: SimpleSelectOption[]) => SimpleSelectOption[]) =>
                setEventFilter("tags", eventFilters.tags, tagOptions, _getValues)
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={eventFilters.hasComments === "true"}
                  onChange={(e) => setEventFilters({ hasComments: e.target.checked ? "true" : "" })}
                  size="medium"
                />
              }
              label="Has Comments?"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: borderRadius.lg,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          boxShadow: shadows.sm,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Advanced Filters
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setIsAdvancedFilters(!isAdvancedFilters)}
            endIcon={isAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              borderRadius: borderRadius.md,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            {isAdvancedFilters ? "Hide Advanced Options" : "Show Advanced Options"}
          </Button>
        </Box>

        <Collapse in={isAdvancedFilters}>
          <Divider sx={{ mb: 2 }} />
          <AdvancedFilters
            eventFilters={eventFilters}
            setEventFilter={setEventFilter}
            setEventFilters={setEventFilters}
          />
        </Collapse>
      </Paper>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1,
        }}
      >
        <SearchButton />
      </Box>
    </Box>
  );
};
